import WebViewer, { Core, WebViewerInstance } from '@pdftron/webviewer'
import { setDefaultViewerSettings } from '~/app/Org/Projects/Submittals/WorkFlow/ScheduleSelection/ScheduleSelection.tsx'
import { headerCustomizations } from 'utilities/webviewer/customizations.tsx'
import { JobManifest } from 'gql/hooks/useJobManifestQuery.ts'
import { useEffect, useRef, useState } from 'react'
import { env } from '~/env.ts'
import { useParams } from 'react-router'
import ConfirmMatchedSpecifiedProductsTable from '~/app/Org/Projects/Submittals/WorkFlow/ConfirmMatchedSpecifiedProducts/ConfirmMatchedSpecifiedProductsTable.tsx'
import {
  getSignedProductsDocument,
  PageAlignmentDetails,
  WorkflowPageAlignmentStepResult,
  WorkflowUploadProductDocStepResult
} from '~/lib/mbue-api/services/workflowStepResult.ts'
import {
  MbueRestAPI,
  WorkflowStepStatus,
  type UpdateTaskCompletionStatusParams
} from 'mbue-rest-client'
import { getString } from '~/lib/storageUtils.ts'
import Loading from '~/components/ui/Loading'
import { useFetchWorkflow } from '~/hooks/submittals'
import { toast } from 'sonner'
import { useFlag } from '~/hooks/useFlag'
import { getFirefoxVersion } from '~/utilities/utils'

const mbueApi = new MbueRestAPI({
  BASE: env.VITE_MBUE_API,
  TOKEN: getString('access_token')
})

const ConfirmMatchedSpecifiedProducts = () => {
  const viewerDivRef = useRef<HTMLDivElement>(null)
  const isViewerInitialized = useRef<boolean>(false)
  const isProdViewerEnabled = useFlag('apryse-prod-viewer-enabled')
  const webViewerInstanceRef = useRef<WebViewerInstance | null>(null)
  const currentHighlightsRef = useRef<Core.Annotations.Annotation[]>([])
  const [isDocumentLoading, setIsDocumentLoading] = useState<boolean>(true)

  const { projectId, workflowInstanceId } = useParams<{
    orgId: string
    projectId: string
    workflowInstanceId: string
  }>()

  const { data: workflowWithData, isLoading: workflowWithDataIsLoading } =
    useFetchWorkflow({
      projectId: parseInt(projectId!, 10),
      workflowInstanceId: workflowInstanceId!
    })

  const updateStepResult = async (
    requestBody: {
      result: WorkflowPageAlignmentStepResult
    },
    taskCompleteStatus: boolean
  ) => {
    if (!projectId || !workflowInstanceId) {
      return
    }

    await mbueApi.submittal.updateStepResult(
      parseInt(projectId),
      workflowInstanceId,
      4,
      requestBody as unknown as never
    )

    if (taskCompleteStatus) {
      await mbueApi.submittal.updateTaskCompletionStatus(
        parseInt(projectId),
        workflowInstanceId,
        4,
        0,
        {
          status: taskCompleteStatus
        } as unknown as UpdateTaskCompletionStatusParams
      )
    }
  }

  const highlightPdfText = (matchedProduct: PageAlignmentDetails) => {
    const instance = webViewerInstanceRef.current

    if (!instance) {
      return
    }

    const { Core } = instance
    const { Annotations, annotationManager } = Core
    const highlightColor = new Annotations.Color(100, 180, 220, 0.4)
    const newHighlights: Core.Annotations.Annotation[] = []

    currentHighlightsRef.current.forEach((highlight) => {
      annotationManager.deleteAnnotation(highlight, { imported: false })
    })
    currentHighlightsRef.current = []

    if (matchedProduct.aligned_page?.length) {
      const firstPage = matchedProduct.aligned_page[0]
      Core.documentViewer.setCurrentPage(firstPage, true)
    }

    if (matchedProduct.is_edited) return

    matchedProduct.coordinates.forEach((coordinate, index) => {
      const pageNumber = matchedProduct.aligned_page?.[index]
      const { xmin, ymin, xmax, ymax } = coordinate
      const rect = new Annotations.RectangleAnnotation({
        PageNumber: pageNumber,
        X: xmin,
        Y: ymin,
        Width: xmax - xmin,
        Height: ymax - ymin,
        StrokeColor: highlightColor,
        FillColor: highlightColor,
        Author: annotationManager.getCurrentUser()
      })

      annotationManager.addAnnotation(rect)
      annotationManager.redrawAnnotation(rect)

      newHighlights.push(rect)
    })

    currentHighlightsRef.current = newHighlights
  }

  useEffect(() => {
    if (isProdViewerEnabled === null) return

    let webViewerKey: string = env.VITE_WEBVIEWER_DEMO_TOKEN ?? ''

    if (isProdViewerEnabled && env.VITE_WEBVIEWER_TOKEN) {
      webViewerKey = env.VITE_WEBVIEWER_TOKEN
    }
    if (!projectId || !workflowInstanceId) {
      console.error(`Project id & workflow instance is needed for product doc`)
      return
    }

    let documentUri = ''
    let webViewerInstance: WebViewerInstance

    const fetchProductDocument = async () => {
      const workflow = await mbueApi.submittal.getWorkflow(
        parseInt(projectId),
        workflowInstanceId
      )
      const product_doc_id = (workflow?.data?.[0] as WorkflowUploadProductDocStepResult)
        ?.doc_id

      if (!product_doc_id) {
        console.error(`Product doc is not found`)
        return
      }

      documentUri = (await getSignedProductsDocument(parseInt(projectId), product_doc_id))
        .product_doc_url
    }

    const initWebViewer = async () => {
      if (isViewerInitialized.current) return

      isViewerInitialized.current = true

      await fetchProductDocument()

      try {
        webViewerInstance = await WebViewer.Iframe(
          {
            path: '/webviewer-0ad25b9a',
            licenseKey: webViewerKey,
            preloadWorker: 'pdf',
            fullAPI: true,
            ui: 'legacy'
          },
          viewerDivRef.current!
        )

        const { Core, UI } = webViewerInstance
        const { documentViewer } = Core

        const version = getFirefoxVersion()

        if (version && version > 145) {
          console.log('Disabling virtual display mode for Firefox version', version)
          const displayMode = Core.documentViewer.getDisplayModeManager()
          displayMode.disableVirtualDisplayMode()
        }

        webViewerInstanceRef.current = webViewerInstance
        documentViewer.addEventListener('documentLoaded', () => {
          setDefaultViewerSettings(webViewerInstance)
          headerCustomizations(
            webViewerInstance,
            { matches: [] } as unknown as JobManifest,
            'product-requirements',
            documentViewer.getPageCount(),
            { isLayerToggleEnabled: false }
          )

          UI.disableFeatures([UI.Feature.Annotations, UI.Feature.NotesPanel])
          setIsDocumentLoading(false)
        })

        void documentViewer.loadDocument(documentUri, { extension: 'pdf' })
      } catch (error) {
        console.error('Error initializing WebViewer:', error)
      }
    }

    void initWebViewer()
  }, [isProdViewerEnabled, projectId, workflowInstanceId])
  const hasShownCompletionToastRef = useRef(false)

  useEffect(() => {
    if (workflowWithDataIsLoading) return

    if (
      !workflowWithDataIsLoading &&
      workflowWithData?.steps?.[4]?.state?.status === WorkflowStepStatus.COMPLETED &&
      !hasShownCompletionToastRef.current
    ) {
      toast.success('Step Already Completed', {
        description: 'This is a read only version of the matched specified products'
      })
      hasShownCompletionToastRef.current = true
    }
  }, [workflowWithData, workflowWithDataIsLoading])

  const isStepCompleted =
    workflowWithData?.steps?.[4]?.state?.status === WorkflowStepStatus.COMPLETED

  return (
    <>
      {isDocumentLoading && <Loading mode="dark" position="fixed" />}
      <div className="w-full h-full flex flex-col">
        <div className="h-[calc(100vh-56px)] w-full grid grid-cols-2">
          <div className="h-full viewer" ref={viewerDivRef} />

          {/* Confirmation section */}
          <ConfirmMatchedSpecifiedProductsTable
            highlightPdfText={highlightPdfText}
            projectId={parseInt(projectId ?? '')}
            workflowInstanceId={workflowInstanceId ?? ''}
            updateStepResult={updateStepResult}
            viewerRef={viewerDivRef}
            isStepCompleted={isStepCompleted}
          />
        </div>
      </div>
    </>
  )
}

export default ConfirmMatchedSpecifiedProducts
